import { useState } from 'react';
import { TrendingDown, ChevronDown, ChevronUp, AlertTriangle, Lightbulb } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePlateauDetection, PlateauedExercise } from '@/hooks/use-plateau-detection';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface PlateauDetectionCardProps {
  clientId: string;
  className?: string;
}

function getSeverityColor(weeks: number): string {
  if (weeks >= 6) return 'text-destructive';
  if (weeks >= 4) return 'text-warning';
  return 'text-warning';
}

function getSeverityBadge(weeks: number): { label: string; variant: 'destructive' | 'default' | 'secondary' } {
  if (weeks >= 6) return { label: 'Severe', variant: 'destructive' };
  if (weeks >= 4) return { label: 'Moderate', variant: 'default' };
  return { label: 'Mild', variant: 'secondary' };
}

function PlateauItem({ exercise }: { exercise: PlateauedExercise }) {
  const [isOpen, setIsOpen] = useState(false);
  const severity = getSeverityBadge(exercise.weeksSinceProgress);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="w-full flex items-center justify-between p-3 hover:bg-muted/50 rounded-lg transition-colors text-left">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-full bg-muted', getSeverityColor(exercise.weeksSinceProgress))}>
              <TrendingDown className="h-4 w-4" />
            </div>
            <div>
              <p className="font-medium">{exercise.exerciseName}</p>
              <p className="text-sm text-muted-foreground">
                {exercise.currentWeight} lbs • {exercise.weeksSinceProgress} weeks stalled
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={severity.variant}>{severity.label}</Badge>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-3 pb-3 pt-1 space-y-3">
          {/* Recent weight history */}
          {exercise.recentWeights.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Recent Max Weights</p>
              <div className="flex gap-2 flex-wrap">
                {exercise.recentWeights.map((w, i) => (
                  <div
                    key={w.week}
                    className={cn(
                      'text-xs px-2 py-1 rounded',
                      i === exercise.recentWeights.length - 1
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {w.maxWeight} lbs
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggestion */}
          <div className="flex gap-2 p-2 bg-info/10 rounded-lg">
            <Lightbulb className="h-4 w-4 text-info mt-0.5 flex-shrink-0" />
            <p className="text-sm text-info">{exercise.suggestedAction}</p>
          </div>

          {/* Last progress date */}
          {exercise.lastProgressDate && (
            <p className="text-xs text-muted-foreground">
              Last weight increase: {exercise.lastProgressDate.toLocaleDateString()}
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function PlateauDetectionCard({ clientId, className }: PlateauDetectionCardProps) {
  const { result, loading, error } = usePlateauDetection(clientId);
  const [showAll, setShowAll] = useState(false);

  if (loading) {
    return (
      <Card className={cn('border', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-40" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !result) {
    return null;
  }

  // Don't show the card if no plateaus detected
  if (!result.hasPlateaus) {
    return (
      <Card className={cn('border bg-success/10 border-success/30', className)}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-success" />
              Plateau Detection
            </span>
            <Badge variant="secondary" className="bg-success/15 text-success">
              All Clear
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No plateaus detected across {result.totalExercisesAnalyzed} exercises analyzed.
            Client is progressing well!
          </p>
        </CardContent>
      </Card>
    );
  }

  const displayExercises = showAll
    ? result.plateauedExercises
    : result.plateauedExercises.slice(0, 3);

  return (
    <Card className={cn('border bg-warning/10 border-warning/30', className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-warning" />
            Plateau Detection
          </span>
          <Badge variant="secondary" className="bg-warning/15 text-warning">
            {result.plateauedExercises.length} Found
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground mb-3">
          These exercises haven't seen weight increases in 3+ weeks
        </p>

        <div className="space-y-1">
          {displayExercises.map(exercise => (
            <PlateauItem key={exercise.exerciseId} exercise={exercise} />
          ))}
        </div>

        {result.plateauedExercises.length > 3 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAll(!showAll)}
            className="w-full mt-2"
          >
            {showAll
              ? 'Show Less'
              : `Show ${result.plateauedExercises.length - 3} More`}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
